import { Button } from '@UILayer/Inputs/Button';
import cn from 'classnames';
import React, { useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';

// CSS
import styles from '@UILayer/UILayer.scss';

// REDUX & MANAGERS
import { getDeviceInfo, getGlobalStyles, getPopupPanel, setPopupPanel } from '@Managers/Redux/AppState/AppState';
import { TPopupPanel } from '@Managers/Redux/AppState/AppState.types';
import { useAppDispatch, useAppSelector } from '@Managers/Redux/Hooks';

// SUBMODULES
import { CurrencyUtils } from '@Core/Currencies/CurrencyUtils';
import TextField from '@UILayer/Inputs/TextField/TextField';
import AutoPlayPanel from './AutoPlayPanel/AutoPlayPanel';
import BetPanel from './BetPanel/BetPanel';
import { IPopupPanelState } from './PopupPanel.types';

const PopupPanel: React.FC<{}> = () => {
    const [state, setState] = useReducer((state: IPopupPanelState, newState: any) => ({ ...state, ...newState }), {
        show: false,
        headerFontSize: undefined,
        contentBaseFont: undefined
    });

    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const currentPanel = useAppSelector(getPopupPanel);
    const device = useAppSelector(getDeviceInfo);
    const globalStyles = useAppSelector(getGlobalStyles);

    const componentCN = cn(
        globalStyles.orientation,
        globalStyles.device,
        globalStyles.wcaMode,
        globalStyles.handMode,
        globalStyles.screenSize,
        globalStyles.isTablet,
        styles.popup_panel_container,
        currentPanel ? styles[currentPanel] : null,
        {
            [styles.horizontal_small]: device.windowOrientationHorizontal && device.appHeight < 360 ? true : false
        }
    );

    useEffect(() => {
        if (currentPanel) {
            setState({ show: true });
        } else {
            setState({ show: false });
        }
    }, [currentPanel]);

    function closePanel() {
        setState({ show: false });
        dispatch(setPopupPanel(false));
    }

    function setPopupHeaderFontSize(value: number) {
        setState({ headerFontSize: value, contentBaseFont: value - 3 });
    }

    function popupHeaderResolver(panel: TPopupPanel | boolean) {
        switch (panel) {
            case TPopupPanel.autoPlay:
                return t(`${panel.toUpperCase()}_HEADER`);
                break;
            case TPopupPanel.bet:
                const currency_label = CurrencyUtils.getConfig().currencyHeaderLabel;
                let header = t(`${panel.toUpperCase()}_HEADER`);

                if (currency_label) {
                    header = t(`POPUP_BET_HEADER_WITH_CURRENCY_LABEL`).replace('%currency_label', currency_label as string);
                }

                return header;
                break;

            default:
                return '';
                break;
        }
    }

    return (
        <>
            {state.show && (
                <div
                    className={componentCN}
                    role="dialog"
                    aria-modal="true"
                    aria-description={currentPanel ? t(`${currentPanel.toUpperCase()}_WCA_POPUP_MESSAGE`) : ''}
                >
                    <div className={styles.popup_panel_container_wrapper}>
                        <div className={styles.popup_panel_header} id={`${currentPanel}_modal_title`}>
                            <div className={styles.popup_panel_title}>
                                <TextField ofHeightValue={1.5} onResizeEnd={(value: number) => setPopupHeaderFontSize(value)}>
                                    {popupHeaderResolver(currentPanel)}
                                </TextField>
                            </div>
                            <div className={styles.popup_panel_close}>
                                <Button
                                    id={`${currentPanel}_modal_button_close`}
                                    description={currentPanel ? t(`${currentPanel.toUpperCase()}_CLOSE_WCA_DESCRIPTION`) : ''}
                                    className={cn(styles.popup_panel_close_button)}
                                    onChange={() => closePanel()}
                                    dataDismiss="modal"
                                ></Button>
                            </div>
                        </div>
                        <div className={styles.popup_panel_content}>
                            {currentPanel === TPopupPanel.bet && <BetPanel baseFontSize={state.contentBaseFont} />}
                            {currentPanel === TPopupPanel.autoPlay && <AutoPlayPanel baseFontSize={state.contentBaseFont} />}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default PopupPanel;

/*
<TextField ofHeightValue={1.5} onResizeEnd={(value: number) => setPopupHeaderFontSize(value)}>
                                    
                                </TextField>
*/
